// Boot sequence (architect task P0-03, §9.3), completed in P0-13; the
// runtime-config round trip was taken off the pre-mount critical path in
// PERF-05 (see below) after Lighthouse traced 600-900ms of pure serial LCP
// render-delay to it on `/` and `/app/login`:
//   1. GET /bff/runtime-config (in the BACKGROUND, not awaited before mount)
//      -> theme tokens, flags, API base into the Pinia configStore — with a
//      safe local fallback (already the store's initial state) applied
//      synchronously so boot never blocks on this network round trip.
//   2. Apply CSS custom properties from the returned theme tokens; set
//      <title>. Point the API client at the resolved `apiBase`. Re-applied
//      once the real config lands, in case it differs from the fallback.
//   3. GET /v1/auth/me -> authStore; on a 401 attempt ONE silent
//      POST /v1/auth/refresh (the HttpOnly refresh cookie survives a reload
//      even though the in-memory access token does not), else stay signed
//      out. See authStore.initSession(). Still awaited before mount — for a
//      first-time guest (the common case, and the one LCP is measured on)
//      this resolves synchronously with no network call (see
//      `hadSessionBefore()`), so it costs ~0ms; the router guard needs a
//      settled auth state before the first navigation resolves.
//   4. Router guard (registered in ../router/index.ts) reads authStore/
//      configStore state on every navigation.
// Called once from main.ts, before `app.use(router)`/`app.mount()`.
import type { Pinia } from 'pinia';
import { configureApiClient } from '@eskayscience/api-client';

import { configureApiBase, getApiOrigin } from '@/lib/api';
import { getAccessToken } from '@/lib/session';
import { useConfigStore } from '@/stores/config';
import { useAuthStore } from '@/stores/auth';

export async function bootApp(pinia: Pinia): Promise<void> {
  const configStore = useConfigStore(pinia);

  // PERF-05: apply the store's built-in fallback config synchronously —
  // zero network cost — so the router/app can mount and paint immediately.
  // The real fetch runs in the background and re-applies on arrival; a
  // fallback-vs-real mismatch (theme tokens, feature flags) is a rare,
  // low-stakes visual reflow, not a correctness issue, and is a deliberate
  // trade for cutting ~700ms of pure serial render-delay off LCP.
  configStore.applyThemeTokens();
  configureApiBase(configStore.runtimeConfig.apiBase);
  void configStore.load().then(() => {
    configStore.applyThemeTokens();
    configureApiBase(configStore.runtimeConfig.apiBase);
    configureApiClient({ baseURL: getApiOrigin(), getAccessToken });
  });

  // Point the generated Orval client (every exam-boards/levels/subjects/
  // sections/tabs/presets composable shares one axios instance) at the API
  // ORIGIN and the SAME in-memory access token the hand-rolled `/v1/auth/*`
  // client uses. Without the token wiring the generated client sends no
  // Authorization header and every authed read 401s (P1-BUG-8).
  //
  // Base URL semantics DIFFER from the hand-rolled `/v1/auth/*` client
  // (P1-BUG-9): the generated orval URLs ALREADY bake in the `/v1` version
  // segment (`{ url: '/v1/exam-boards' }`), so this client must be given the
  // ORIGIN WITHOUT `/v1` — otherwise axios composes `baseURL + url` =
  // `/v1/v1/exam-boards` → 404. `getApiOrigin()` strips the trailing `/v1`
  // from the same resolved runtime base (`'/v1'` → `''` same-origin,
  // `https://host/v1` → `https://host`), yielding single-prefixed
  // `/v1/exam-boards` requests. `getAccessToken()` returns null when signed
  // out, so guest calls stay unauthenticated.
  configureApiClient({ baseURL: getApiOrigin(), getAccessToken });

  const authStore = useAuthStore(pinia);
  await authStore.initSession();

  // P2-25 command poller (design §5.1): start it after a successful cold-load
  // session recovery, same as every other session-establishing entry point
  // (`authStore.applySession`) — only ever when authenticated. A failed/absent
  // refresh leaves `isAuthenticated` false and the poller stays stopped.
  if (authStore.isAuthenticated) {
    authStore.startCommandPoller();
  }
}
